/**
 * FC Localization Modal
 * Handles modal open/close, custom dropdowns, locale detection, and persistence.
 */
if (!customElements.get('fc-localization-modal')) {
  customElements.define(
    'fc-localization-modal',
    class FcLocalizationModal extends HTMLElement {
      constructor() {
        super();
        this.overlay = this.querySelector('.fc-loc-modal__overlay');
        this.closeBtn = this.querySelector('.fc-loc-modal__close');
        this.form = this.querySelector('form');
        this.countryInput = this.querySelector('input[name="country_code"]');
        this.languageInput = this.querySelector('input[name="locale_code"]');
        this.dropdowns = this.querySelectorAll('.fc-loc-modal__dropdown');
        this.previouslyFocused = null;

        this.STORAGE_KEY = 'fc-locale-preference-set';
      }

      connectedCallback() {
        this.closeBtn.addEventListener('click', this.close.bind(this));
        this.overlay.addEventListener('click', this.onOverlayClick.bind(this));
        document.addEventListener('keydown', this.onKeyDown.bind(this));

        document.querySelectorAll('.localization-modal').forEach((trigger) => {
          trigger.addEventListener('click', (e) => {
            e.preventDefault();
            this.open();
          });
        });

        this.hijackLocalizationForms();

        this.dropdowns.forEach((dropdown) => {
          var trigger = dropdown.querySelector('.fc-loc-modal__dropdown-trigger');
          var items = dropdown.querySelectorAll('.fc-loc-modal__dropdown-item');

          trigger.addEventListener('click', (e) => {
            e.preventDefault();
            this.toggleDropdown(dropdown);
          });

          items.forEach((item) => {
            item.addEventListener('click', (e) => {
              e.preventDefault();
              this.selectItem(dropdown, item);
            });
          });
        });

        this.overlay.addEventListener('click', this.onClickOutsideDropdown.bind(this));

        this.checkFirstVisit();
      }

      open() {
        this.previouslyFocused = document.activeElement;
        this.overlay.setAttribute('open', '');
        document.body.style.overflow = 'hidden';
        this.closeBtn.focus();
        this.trapFocus();
      }

      close() {
        this.overlay.removeAttribute('open');
        document.body.style.overflow = '';
        this.dropdowns.forEach((d) => d.removeAttribute('open'));

        if (this.previouslyFocused) {
          this.previouslyFocused.focus();
          this.previouslyFocused = null;
        }
      }

      onOverlayClick(e) {
        if (e.target === this.overlay) {
          this.close();
        }
      }

      onKeyDown(e) {
        if (e.key === 'Escape' && this.overlay.hasAttribute('open')) {
          this.close();
        }
      }

      onClickOutsideDropdown(e) {
        if (!e.target.closest('.fc-loc-modal__dropdown')) {
          this.dropdowns.forEach(function (d) { d.removeAttribute('open'); });
        }
      }

      toggleDropdown(dropdown) {
        var isOpen = dropdown.hasAttribute('open');

        this.dropdowns.forEach((d) => d.removeAttribute('open'));

        if (!isOpen) {
          dropdown.setAttribute('open', '');
        }
      }

      selectItem(dropdown, item) {
        var type = dropdown.dataset.type;
        var value = item.dataset.value;
        var label = item.dataset.label;
        var extra = item.dataset.extra || '';

        dropdown.querySelectorAll('.fc-loc-modal__dropdown-item').forEach((i) => {
          i.setAttribute('aria-selected', 'false');
        });
        item.setAttribute('aria-selected', 'true');

        var labelEl = dropdown.querySelector('.fc-loc-modal__dropdown-label');
        var valueEl = dropdown.querySelector('.fc-loc-modal__dropdown-value-text');

        if (labelEl) labelEl.textContent = label;
        if (valueEl) valueEl.textContent = extra;

        if (type === 'country' && this.countryInput) {
          this.countryInput.value = value;
        } else if (type === 'language' && this.languageInput) {
          this.languageInput.value = value;
        }

        dropdown.removeAttribute('open');
      }

      trapFocus() {
        var modal = this.overlay;
        var focusableEls = modal.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (focusableEls.length === 0) return;

        var firstEl = focusableEls[0];
        var lastEl = focusableEls[focusableEls.length - 1];

        var handler = function (e) {
          if (e.key !== 'Tab') return;
          if (!modal.hasAttribute('open')) {
            modal.removeEventListener('keydown', handler);
            return;
          }
          if (e.shiftKey) {
            if (document.activeElement === firstEl) {
              e.preventDefault();
              lastEl.focus();
            }
          } else {
            if (document.activeElement === lastEl) {
              e.preventDefault();
              firstEl.focus();
            }
          }
        };

        modal.addEventListener('keydown', handler);
      }

      hijackLocalizationForms() {
        var self = this;
        document.querySelectorAll('localization-form .disclosure__button').forEach(function (btn) {
          btn.addEventListener('click', function (e) {
            e.preventDefault();
            e.stopImmediatePropagation();

            var panel = btn.closest('localization-form').querySelector('.disclosure__list-wrapper');
            if (panel) panel.setAttribute('hidden', '');
            btn.setAttribute('aria-expanded', 'false');

            self.open();
          }, true);
        });
      }

      checkFirstVisit() {
        if (localStorage.getItem(this.STORAGE_KEY)) return;

        var browserLang = (navigator.language || navigator.userLanguage || '').toLowerCase();
        var suggestedCountry = this.mapLocaleToCountry(browserLang);

        if (suggestedCountry && this.countryInput) {
          var currentCountry = this.countryInput.value;
          if (suggestedCountry !== currentCountry) {
            this.preselectCountry(suggestedCountry);
            this.open();
          }
        }

        if (browserLang) {
          var langCode = browserLang.split('-')[0];
          this.preselectLanguage(langCode);
        }

        localStorage.setItem(this.STORAGE_KEY, 'true');
      }

      mapLocaleToCountry(locale) {
        var map = {
          'en-us': 'US',
          'en-gb': 'GB',
          'en-au': 'AU',
          'en-ca': 'CA',
          'nl': 'NL',
          'nl-nl': 'NL',
          'nl-be': 'BE',
          'de': 'DE',
          'de-de': 'DE',
          'de-at': 'AT',
          'de-ch': 'CH',
          'fr': 'FR',
          'fr-fr': 'FR',
          'fr-be': 'BE',
          'fr-ca': 'CA',
          'fr-ch': 'CH',
          'es': 'ES',
          'es-es': 'ES',
          'it': 'IT',
          'it-it': 'IT',
          'pt': 'PT',
          'pt-br': 'BR',
          'ja': 'JP',
          'ko': 'KR',
          'zh': 'CN',
          'zh-cn': 'CN',
          'zh-tw': 'TW',
        };

        if (map[locale]) return map[locale];
        var lang = locale.split('-')[0];
        return map[lang] || null;
      }

      preselectCountry(countryCode) {
        var dropdown = this.querySelector('.fc-loc-modal__dropdown[data-type="country"]');
        if (!dropdown) return;

        var item = dropdown.querySelector('[data-value="' + countryCode + '"]');
        if (item) this.selectItem(dropdown, item);
      }

      preselectLanguage(langCode) {
        var dropdown = this.querySelector('.fc-loc-modal__dropdown[data-type="language"]');
        if (!dropdown) return;

        var item = dropdown.querySelector('[data-value="' + langCode + '"]');
        if (item) this.selectItem(dropdown, item);
      }

      onFormSubmit() {
        localStorage.setItem(this.STORAGE_KEY, 'true');
      }
    }
  );
}
